import { NO_CATEGORY_FILTER } from '@klopotun/contracts';
import { useCallback } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useCategoriesQuery } from '../../entities/category/index.js';
import { type MerchantSpending, useSummaryQuery } from '../../entities/summary/index.js';
import {
  MERCHANT_SEARCH_PARAM,
  operationDetailPath,
  summaryMerchantPath,
} from '../../features/navigation/index.js';
import {
  categoryTitleOf,
  formatBaseMoney,
  useChosenPeriod,
} from '../../features/summary-view/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import { ErrorState, PageHeader, Screen } from '../../shared/ui/index.js';
import { OperationList } from '../../widgets/bank-operations/index.js';
import {
  FigureCard,
  FigureNote,
  spendingLabelOf,
  SummaryFailureScreen,
  SummaryLoadingScreen,
} from '../../widgets/year-summary/index.js';
import styles from './summary-merchant-page.module.css';

const UNKNOWN_MERCHANT_TEXT = 'Продавець';

const UNKNOWN_CATEGORY_TEXT = 'невідома';

const isMerchantRow =
  (category: string, merchantKey: string) =>
  (merchant: MerchantSpending): boolean => {
    const isSameKey = merchant.merchantKey === merchantKey;

    return isSameKey && (merchant.categoryId ?? NO_CATEGORY_FILTER) === category;
  };

const isMovedMerchantRow =
  (category: string, merchantKey: string) =>
  (merchant: MerchantSpending): boolean => {
    const isSameKey = merchant.merchantKey === merchantKey;

    return isSameKey && (merchant.categoryId ?? NO_CATEGORY_FILTER) !== category;
  };

export const SummaryMerchantPage = () => {
  const { category = NO_CATEGORY_FILTER } = useParams();
  const [searchParams] = useSearchParams();
  const merchantKey = searchParams.get(MERCHANT_SEARCH_PARAM) ?? '';
  const navigate = useNavigate();
  const { chosen } = useChosenPeriod();
  const summary = useSummaryQuery(chosen.year, chosen.month, category);
  const everyCategory = useSummaryQuery(chosen.year, chosen.month);
  const categories = useCategoriesQuery({ includeArchived: true });
  const goBack = useCallback(() => {
    void navigate(-1);
  }, [navigate]);

  useBackButton(goBack);

  if (merchantKey === '') {
    return (
      <Screen>
        <ErrorState
          title="Продавця не вказано"
          message="Поверніться до категорії й оберіть продавця ще раз."
        />
      </Screen>
    );
  }

  if (summary.isPending) {
    return <SummaryLoadingScreen />;
  }

  if (summary.isError) {
    return (
      <SummaryFailureScreen
        message={summary.error.message}
        onRetry={() => {
          void summary.refetch();
        }}
      />
    );
  }

  const loaded = summary.data;
  const merchant = loaded.merchants.find(isMerchantRow(category, merchantKey));
  const isMerchantMissing = merchant === undefined;

  if (isMerchantMissing && everyCategory.isPending) {
    return <SummaryLoadingScreen />;
  }

  const movedTo = isMerchantMissing
    ? everyCategory.data?.merchants.find(isMovedMerchantRow(category, merchantKey))
    : undefined;

  if (movedTo !== undefined) {
    return (
      <Navigate
        replace
        to={summaryMerchantPath(movedTo.categoryId ?? NO_CATEGORY_FILTER, merchantKey, chosen)}
      />
    );
  }

  const record = (categories.data ?? []).find((candidate) => candidate.id === category);
  const isUncategorised = category === NO_CATEGORY_FILTER;
  const categoryTitle =
    isUncategorised || record !== undefined ? categoryTitleOf(record) : UNKNOWN_CATEGORY_TEXT;

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title={merchant?.name ?? UNKNOWN_MERCHANT_TEXT} />

        {merchant !== undefined && (
          <FigureCard
            label={spendingLabelOf(loaded)}
            value={formatBaseMoney(merchant.spentMinor, loaded.baseCurrency)}
          >
            <FigureNote>Операцій: {merchant.operationCount}</FigureNote>
            <FigureNote>Категорія: {categoryTitle}</FigureNote>
          </FigureCard>
        )}

        <OperationList
          filter={{ year: chosen.year, month: chosen.month, categoryId: category, merchantKey }}
          baseCurrency={loaded.baseCurrency}
          onOpen={(operationId) => {
            void navigate(operationDetailPath(operationId));
          }}
        />
      </div>
    </Screen>
  );
};
